import { useState, useEffect, useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import {
  fundStatusMap,
  fundStatusToLabel,
} from 'adminConstants/fundProperties';
import { TRANCHE_REDESIGN } from 'adminConstants/routes';
import { useFilters, usePagination } from 'further-ui/hooks';
import { useFunds, exportFunds } from 'hooks/data/fund/useFunds';
import { usePerformanceFeeSimulation } from 'hooks/data/fund/usePerformanceFeeSimulation';
import { useTags } from 'hooks/data/tag/useTags';
import useApiExceptionHandler from 'hooks/ui/useApiExceptionHandler';
import { useGetRole } from 'hooks/ui/useGetRole';
import TrancheList from './TrancheList';
import { TrancheListFilters, TrancheListProps } from './TrancheList/types';
import TrancheListRedesign from './TrancheList/Redesign';

export const url = process.env.REACT_APP_STAGE;

const fundStatus = Object.entries(fundStatusToLabel).map(([value, name]) => ({
  name,
  value,
}));

const Tranche = () => {
  const [isExportLoading, setIsExportLoading] = useState(false);
  const exceptionHandler = useApiExceptionHandler();
  const pagination = usePagination({
    id: 'tranches-table',
  });
  const today = useMemo(() => new Date(), []);

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const fundStatusParam = qs.get('fundStatus');
    if (fundStatusParam) {
      const foundStatus = fundStatus.find(
        (status) => status.value === fundStatusParam,
      );
      if (foundStatus) {
        handleChangeFilters({ fundStatus: foundStatus });
      }
    }
  }, []);

  const { filters, handleChangeFilters } = useFilters<TrancheListFilters>(
    'tranches-table',
    {
      defaultFilters: {
        firmId: undefined,
        fundStatus: null,
        tagId: null,
        fundName: '',
        dateRange: undefined,
      },
      onFiltersChange: pagination.toFirstPage,
    },
  );

  const queryParams = {
    firmId: filters?.firmId,
    fundStatus: filters?.fundStatus?.value
      ? [filters.fundStatus.value]
      : undefined,
    tagId: filters.tagId?._id,
    fundName: filters.fundName || undefined,
    startDate: filters.dateRange?.startDate.startOfDay(),
    endDate: filters.dateRange?.endDate.startOfDay(),
    includeInvestmentsSummary: true,
    snapshotDate: today,
    keyName: pagination.orderBy,
    page: pagination.page === 0 ? 1 : pagination.page,
    perPage: pagination.rowsPerPage,
    type: pagination.order,
  };

  const { isSuperAdmin } = useGetRole();

  const { tags } = useTags();
  const { tranches } = useFunds(queryParams);
  const { exportPerformanceFeeSimulation } = usePerformanceFeeSimulation();

  const handleExportFunds = async () => {
    try {
      setIsExportLoading(true);
      await exportFunds({
        ...queryParams,
        includeExportData: true,
        includeChargedPerformanceFees: true,
      });
    } catch (e: any) {
      exceptionHandler(e, 'Error when exporting tranches', true);
    } finally {
      setIsExportLoading(false);
    }
  };

  const handlePerformanceFeeSimulation = async () => {
    try {
      setIsExportLoading(true);

      const params = {
        firmId: filters.firmId ?? undefined,
        tagId: filters?.tagId?._id ?? undefined,
        startDate: filters.dateRange?.startDate,
        endDate: filters.dateRange?.endDate,
        fundStatus: filters?.fundStatus?.value
          ? [filters.fundStatus.value]
          : undefined,
        fullyDeployed:
          filters?.fundStatus?.value === String(fundStatusMap.open)
            ? true
            : undefined,
        fundName: filters.fundName || undefined,
      };

      await exportPerformanceFeeSimulation(params);
    } catch (e: unknown) {
      exceptionHandler(
        e instanceof Error ? e : new Error('Unknown error'),
        'Error when exporting performance fee simulation',
        true,
      );
    } finally {
      setIsExportLoading(false);
    }
  };

  const props: TrancheListProps = {
    isSuperAdmin,
    filters,
    handleChangeFilters,
    fundStatus,
    tags,
    isExportLoading,
    handleExportFunds,
    handlePerformanceFeeSimulation,
    pagination,
    tranches,
  };

  const isRedesign = useRouteMatch(TRANCHE_REDESIGN);

  if (isRedesign) {
    return <TrancheListRedesign {...props} />;
  }

  return <TrancheList {...props} />;
};

export default Tranche;
